import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import restaurantService, { UserOrderDetails } from '../../services/restaurantService';
import { useRealtime } from '../../contexts/RealtimeContext';
import {
  extractAppNameFromPath,
  isKioskRoute,
  shouldShowOngoingOrderBar,
} from '../../utils/restaurantRoutes';
import { kioskOrdersPath } from '../Kiosk/kioskPaths';
import { useKioskMode } from '../../contexts/KioskModeContext';
import {
  clearOngoingOrderContext,
  notifyOrderCompleted,
  ORDER_PLACED_EVENT,
  readOngoingOrderContext,
  saveOngoingOrderContext,
} from '../../utils/ongoingOrderContext';
import { getFixedChromePortalTarget } from '../../utils/fixedChromePortal';
import {
  isCompletedOrderStatus,
  isPaymentIncompleteStatus,
  isTerminalOrderStatus,
  keepFreshGuestOrderStatus,
  resolveGuestOrderStatus,
  resolveGuestOrderStep,
  itemStatusLabel,
  orderStatusHint,
  orderStatusLabel,
  ORDER_PROGRESS_STEPS,
} from '../../utils/orderStatus';
import {
  deliveryHeadline,
  deliveryHint,
  deliveryStepIndex,
  DELIVERY_PROGRESS_STEPS,
  isTerminalRiderStatus,
  keepFreshDeliveryTrackingStatus,
  resolveDeliveryGuestOrderStatus,
} from '../../utils/deliveryGuestStatus';
import useOrderPolling from '../../hooks/useOrderPolling';
import deliveryService from '../../services/deliveryService';
import { abandonGuestPayment, getOrderPayment, isOpenPaymentStatus } from '../../services/paymentService';
import MenuItemThumbnail from '../common/MenuItemThumbnail';
import './OngoingOrderBar.css';
import OrderLineOptions from '../common/OrderLineOptions/OrderLineOptions';
import { guestItemGroups } from '../../utils/guestItemGroups';

const PulsingDot: React.FC = () => (
  <span className="ongoing-order-pulse" aria-hidden="true">
    <span className="ongoing-order-pulse__ring" />
    <span className="ongoing-order-pulse__core" />
  </span>
);

interface StepTrackerProps {
  step: number;
  steps?: readonly string[];
}

const StepTracker: React.FC<StepTrackerProps> = ({ step, steps = ORDER_PROGRESS_STEPS }) => (
  <div className="ongoing-order-steps-scroll">
    <div className="ongoing-order-steps" style={{ ['--step-count' as string]: String(steps.length) }}>
      {steps.map((label, index) => {
        const done = index < step;
        const active = index === step;
        const last = index === steps.length - 1;
        return (
          <div
            key={label}
            className={`ongoing-order-step-wrap${last ? '' : ' ongoing-order-step-wrap--grow'}`}
          >
            <div className="ongoing-order-step">
              <div
                className={`ongoing-order-step__dot${done ? ' is-done' : ''}${active ? ' is-active' : ''}`}
              >
                {done ? (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <path
                      d="M2 6l3 3 5-5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : active ? (
                  <PulsingDot />
                ) : null}
              </div>
              <span className={`ongoing-order-step__label${done || active ? ' is-emphasis' : ''}`}>
                {label}
              </span>
            </div>
            {!last && <div className={`ongoing-order-step__line${done ? ' is-done' : ''}`} />}
          </div>
        );
      })}
    </div>
  </div>
);

function decodeCurrency(symbol?: string): string {
  if (!symbol) return '₹';
  if (symbol.includes('&#')) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = symbol;
    return tempDiv.textContent || tempDiv.innerText || '₹';
  }
  return symbol;
}

function formatPrice(amount: number | undefined, currencySymbol?: string): string {
  const sym = decodeCurrency(currencySymbol);
  const value = amount ?? 0;
  return `${sym}${value.toFixed(0)}`;
}

const OngoingOrderBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user: signedInUser } = useAppSelector((state) => state.user);
  const { enabled: kioskShell } = useKioskMode();
  const { subscribeOrderStatus } = useRealtime();

  const [order, setOrder] = useState<UserOrderDetails | null>(null);
  const [appName, setAppName] = useState<string | null>(null);
  const [restaurantName, setRestaurantName] = useState('');
  const [expanded, setExpanded] = useState(false);
  const mobileBarRef = useRef<HTMLDivElement>(null);
  const [dismissed, setDismissed] = useState(false);
  const [openPaymentId, setOpenPaymentId] = useState<string | null>(null);
  const [cancellingPayment, setCancellingPayment] = useState(false);
  const [deliveryTrackingStatus, setDeliveryTrackingStatus] = useState<string | undefined>();
  const orderRef = useRef<UserOrderDetails | null>(null);
  orderRef.current = order;
  const terminalOrderIdRef = useRef<number | null>(null);

  const pathAppName = extractAppNameFromPath(location.pathname);
  const pageEligible = shouldShowOngoingOrderBar(location.pathname);

  const resolveAppName = useCallback((): string | null => {
    if (pathAppName) return pathAppName;
    return readOngoingOrderContext()?.appName ?? null;
  }, [pathAppName]);

  const loadOngoingOrder = useCallback(async () => {
    const targetApp = resolveAppName();
    if (!targetApp || !isAuthenticated) {
      setOrder(null);
      setAppName(null);
      return;
    }

    try {
      const response = await restaurantService.getOngoingOrder(targetApp);
      if (response.success && response.hasOngoingOrder && response.order) {
        if (
          terminalOrderIdRef.current != null &&
          Number(response.order.orderId) === terminalOrderIdRef.current
        ) {
          setOrder(null);
          setAppName(null);
          setDeliveryTrackingStatus(undefined);
          return;
        }
        const status = keepFreshGuestOrderStatus(
          orderRef.current?.userOrder?.orderStatus,
          response.order.userOrder?.orderStatus
        );
        if (status && (isTerminalOrderStatus(status) || isPaymentIncompleteStatus(status))) {
          terminalOrderIdRef.current = response.order.orderId;
          if (isCompletedOrderStatus(status)) {
            notifyOrderCompleted(targetApp, response.order.orderId);
          }
          clearOngoingOrderContext();
          setOrder(null);
          setAppName(null);
          return;
        }
        const nextOrder =
          status && status !== response.order.userOrder?.orderStatus
            ? {
                ...response.order,
                userOrder: { ...response.order.userOrder, orderStatus: status },
              }
            : response.order;
        setOrder(nextOrder);
        setAppName(targetApp);
        if (response.order.userOrder?.orderType === 2 && response.order.orderId) {
          deliveryService
            .tracking(targetApp, response.order.orderId)
            .then((data) => {
              if (data?.success && data.status) {
                setDeliveryTrackingStatus((prev) =>
                  keepFreshDeliveryTrackingStatus(prev, data.status)
                );
              }
            })
            .catch(() => undefined);
        } else {
          setDeliveryTrackingStatus(undefined);
        }
        saveOngoingOrderContext({
          appName: targetApp,
          orderId: response.order.orderId,
        });
      } else {
        if (!pathAppName) {
          clearOngoingOrderContext();
        }
        setOrder(null);
        setAppName(null);
        setDeliveryTrackingStatus(undefined);
      }
    } catch {
      setOrder(null);
      setDeliveryTrackingStatus(undefined);
    }
  }, [resolveAppName, isAuthenticated, pathAppName]);

  useEffect(() => {
    if (!pageEligible || !isAuthenticated) {
      setOrder(null);
      return;
    }
    loadOngoingOrder();
  }, [pageEligible, isAuthenticated, location.pathname, loadOngoingOrder]);

  useEffect(() => {
    const current = order?.userOrder;
    const orderId = order?.orderId;
    if (!current?.restroId || !orderId || current.paymentStatus === 'PAID') {
      setOpenPaymentId(null);
      return undefined;
    }
    let cancelled = false;
    getOrderPayment(orderId, current.restroId).then((payment) => {
      if (cancelled) return;
      setOpenPaymentId(payment && isOpenPaymentStatus(payment.status) ? payment.paymentId : null);
    });
    return () => {
      cancelled = true;
    };
  }, [order?.orderId, order?.userOrder?.restroId, order?.userOrder?.paymentStatus]);

  const handleCancelOpenPayment = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!openPaymentId || cancellingPayment) return;
    setCancellingPayment(true);
    abandonGuestPayment(openPaymentId, 'CANCELLED')
      .then(() => setOpenPaymentId(null))
      .catch(() => undefined)
      .finally(() => setCancellingPayment(false));
  };

  useEffect(() => {
    if (!appName) return;
    let cancelled = false;
    restaurantService.getRestaurant(appName).then((res) => {
      if (!cancelled && res.success && res.restaurant?.restaurantName) {
        const name = res.restaurant.restaurantName;
        setRestaurantName(name);
        if (order?.orderId) {
          saveOngoingOrderContext({ appName, orderId: order.orderId, restaurantName: name });
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [appName, order?.orderId]);

  useEffect(() => {
    const handler = () => loadOngoingOrder();
    window.addEventListener(ORDER_PLACED_EVENT, handler);
    return () => window.removeEventListener(ORDER_PLACED_EVENT, handler);
  }, [loadOngoingOrder]);

  useEffect(() => {
    if (!pageEligible || !isAuthenticated) return undefined;
    return subscribeOrderStatus((payload) => {
      const current = orderRef.current;
      if (!current?.orderId) {
        void loadOngoingOrder();
        return;
      }
      if (Number(payload.orderId) !== current.orderId) return;

      if (payload.deliveryStatus) {
        setDeliveryTrackingStatus((prev) =>
          keepFreshDeliveryTrackingStatus(prev, payload.deliveryStatus)
        );
      }

      const nextStatus = payload.orderStatus || payload.status;
      const terminal =
        (nextStatus && (isTerminalOrderStatus(nextStatus) || isPaymentIncompleteStatus(nextStatus))) ||
        isTerminalRiderStatus(payload.deliveryStatus);
      if (terminal) {
        if (appName && current.orderId && nextStatus && isCompletedOrderStatus(nextStatus)) {
          notifyOrderCompleted(appName, current.orderId);
        }
        terminalOrderIdRef.current = current.orderId;
        clearOngoingOrderContext();
        setOrder(null);
        setAppName(null);
        setDeliveryTrackingStatus(undefined);
        return;
      }

      // Always refetch — staff pushes often omit item rows; delivery sync can trail CometD.
      void loadOngoingOrder();
    });
  }, [pageEligible, isAuthenticated, subscribeOrderStatus, appName, loadOngoingOrder]);

  useOrderPolling(
    loadOngoingOrder,
    pageEligible && isAuthenticated,
  );

  useEffect(() => {
    const barVisible = Boolean(order && pageEligible && !dismissed);
    const onCartPage = location.pathname.includes('/cart');
    document.body.classList.toggle('ongoing-order-bar-visible', barVisible);
    document.body.classList.toggle('ongoing-order-bar-on-cart', barVisible && onCartPage);
    return () => {
      document.body.classList.remove('ongoing-order-bar-visible');
      document.body.classList.remove('ongoing-order-bar-on-cart');
    };
  }, [order, pageEligible, dismissed, location.pathname]);

  // Keep layout offsets in sync with the real bar height (collapsed vs expanded).
  useEffect(() => {
    const node = mobileBarRef.current;
    if (!node) {
      document.documentElement.style.removeProperty('--ongoing-order-mobile-bar-height');
      return undefined;
    }

    const syncHeight = () => {
      const height = Math.ceil(node.getBoundingClientRect().height);
      if (height > 0) {
        document.documentElement.style.setProperty(
          '--ongoing-order-mobile-bar-height',
          `${height}px`
        );
      }
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [order, pageEligible, dismissed, expanded, location.pathname]);

  const itemLines = useMemo(
    () =>
      (order?.userOrderItemsList || []).map((i) => ({
        status: (i as { status?: string }).status,
        active: (i as { active?: boolean }).active !== false,
      })),
    [order?.userOrderItemsList]
  );

  const isDeliveryOrder = order?.userOrder?.orderType === 2;

  const effectiveOrderStatus = useMemo(() => {
    if (isDeliveryOrder) {
      return resolveDeliveryGuestOrderStatus(
        order?.userOrder?.orderStatus,
        deliveryTrackingStatus,
        itemLines
      );
    }
    return resolveGuestOrderStatus(order?.userOrder?.orderStatus, itemLines);
  }, [isDeliveryOrder, itemLines, order?.userOrder?.orderStatus, deliveryTrackingStatus]);

  const step = useMemo(() => {
    if (isDeliveryOrder) {
      return Math.max(0, deliveryStepIndex(effectiveOrderStatus, deliveryTrackingStatus));
    }
    return resolveGuestOrderStep(order?.userOrder?.orderStatus, itemLines);
  }, [isDeliveryOrder, itemLines, order?.userOrder?.orderStatus, effectiveOrderStatus, deliveryTrackingStatus]);

  const progressSteps = isDeliveryOrder ? DELIVERY_PROGRESS_STEPS : ORDER_PROGRESS_STEPS;

  const activeItems = useMemo(
    () =>
      (order?.userOrderItemsList || []).filter(
        (item) => (item as { active?: boolean }).active !== false
      ),
    [order?.userOrderItemsList]
  );

  const itemCount = activeItems.reduce((sum, item) => sum + (item.quantity || 0), 0) ?? 0;
  const firstItem = activeItems[0];
  const orderCode = order?.userOrder?.orderCode || (order?.orderId ? `#${order.orderId}` : '');
  const statusText = isDeliveryOrder
    ? deliveryHeadline(effectiveOrderStatus, deliveryTrackingStatus)
    : orderStatusLabel(effectiveOrderStatus);
  const statusHint = isDeliveryOrder
    ? deliveryHint(effectiveOrderStatus, deliveryTrackingStatus)
    : orderStatusHint(effectiveOrderStatus);

  const handleNavigate = () => {
    if (!appName || !order?.orderId) return;
    if (kioskShell || isKioskRoute(location.pathname)) {
      navigate(kioskOrdersPath(appName, order.orderId));
      return;
    }
    navigate(`/restaurants/${appName}/orders/${order.orderId}`);
  };

  if (kioskShell || !pageEligible || !isAuthenticated || !order || !appName || dismissed) {
    return null;
  }

  const summaryLine = `${itemCount} item${itemCount !== 1 ? 's' : ''} · ${restaurantName || 'Restaurant'} · Order ${orderCode}`;

  const itemGroups = guestItemGroups(activeItems, signedInUser?.id);
  const itemStatusList = activeItems.length > 0 ? (
    <ul
      className={`ongoing-order-item-status-list${itemGroups ? ' ongoing-order-item-status-list--grouped' : ''}`}
      aria-label="Item statuses"
    >
      {(itemGroups || [{ key: 'all', label: '', isYou: false, isStaff: false, items: activeItems }]).map((group) => (
        <React.Fragment key={group.key}>
          {itemGroups && (
            <li className="ongoing-order-guest-head">
              <i className={`fa ${group.isStaff ? 'fa-cutlery' : 'fa-user'}`} aria-hidden />
              <span>{group.label}</span>
              {group.isYou ? <span className="ongoing-order-guest-head__you">You</span> : null}
              {group.isStaff && group.label !== 'Restaurant' ? (
                <span className="ongoing-order-guest-head__role">Restaurant</span>
              ) : null}
            </li>
          )}
          {group.items.map((item) => {
            const status = (item as { status?: string }).status;
            return (
              <li key={item.id ?? `${item.menuId}-${item.itemName}`} className="ongoing-order-item-status-row">
                <span className="ongoing-order-item-status-row__name">
                  {item.quantity}x {item.itemName}
                  <OrderLineOptions optionsNote={item.optionsNote} itemNote={item.itemNote} />
                </span>
                <span className="ongoing-order-item-status-pill">
                  {status ? itemStatusLabel(status) : '—'}
                </span>
              </li>
            );
          })}
        </React.Fragment>
      ))}
    </ul>
  ) : null;

  const barContent = (
    <>
      <div className="ongoing-order-desktop" role="region" aria-label="Ongoing order">
        <div className="ongoing-order-desktop__inner">
          <div className="ongoing-order-desktop__left">
            <div className="ongoing-order-icon-badge" aria-hidden="true">
              <span>🍽️</span>
            </div>
            <div>
              <div className="ongoing-order-status-row">
                <PulsingDot />
                <span className="ongoing-order-status-text">ORDER IN PROGRESS</span>
              </div>
              <div className="ongoing-order-meta">
                {orderCode} · {itemCount} item{itemCount !== 1 ? 's' : ''}
                {openPaymentId ? ' · Payment in progress' : ''}
              </div>
            </div>
          </div>

          <div className="ongoing-order-desktop__steps">
            <StepTracker step={step} steps={progressSteps} />
          </div>

          <div className="ongoing-order-desktop__actions">
            {openPaymentId && (
              <button
                type="button"
                className="ongoing-order-cta ongoing-order-cta--ghost"
                onClick={handleCancelOpenPayment}
                disabled={cancellingPayment}
              >
                {cancellingPayment ? 'Cancelling…' : 'Cancel payment'}
              </button>
            )}
            <button type="button" className="ongoing-order-cta" onClick={handleNavigate}>
              View Order Details →
            </button>
          </div>
          <button
            type="button"
            className="ongoing-order-dismiss ongoing-order-dismiss--desktop"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss ongoing order banner"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="ongoing-order-mobile" ref={mobileBarRef} role="region" aria-label="Ongoing order">
        {expanded && (
          <button
            type="button"
            className="ongoing-order-mobile__backdrop"
            onClick={() => setExpanded(false)}
            aria-label="Close order details"
          />
        )}
        <div className={`ongoing-order-mobile__panel${expanded ? ' is-expanded' : ''}`}>
          <div className="ongoing-order-mobile__handle-wrap">
            <button
              type="button"
              className="ongoing-order-mobile__handle"
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? 'Collapse order panel' : 'Expand order panel'}
            />
          </div>

          <div
            className="ongoing-order-mobile__row"
            onClick={() => setExpanded((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setExpanded((v) => !v);
              }
            }}
            role="button"
            tabIndex={0}
          >
            <div className="ongoing-order-mobile__summary">
              <div className="ongoing-order-icon-badge ongoing-order-icon-badge--sm" aria-hidden="true">
                <span>🍽️</span>
              </div>
              <div>
                <div className="ongoing-order-status-row">
                  <PulsingDot />
                  <span className="ongoing-order-status-text ongoing-order-status-text--sm">
                    {statusText.toUpperCase()}
                  </span>
                </div>
                <div className="ongoing-order-meta ongoing-order-meta--sm">
                  {summaryLine}
                  {openPaymentId ? ' · Payment in progress' : ''}
                </div>
              </div>
            </div>
            <div className="ongoing-order-mobile__row-actions">
              <button
                type="button"
                className="ongoing-order-cta ongoing-order-cta--sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigate();
                }}
              >
                View Order
              </button>
              <span className={`ongoing-order-chevron${expanded ? ' is-open' : ''}`} aria-hidden="true">
                ⌃
              </span>
              <button
                type="button"
                className="ongoing-order-dismiss ongoing-order-dismiss--mobile"
                onClick={(e) => {
                  e.stopPropagation();
                  setDismissed(true);
                }}
                aria-label="Dismiss ongoing order banner"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>

          {expanded && (
            <div className="ongoing-order-mobile__expanded">
              <StepTracker step={step} steps={progressSteps} />
              {itemStatusList}
              {firstItem && (
              <div className="ongoing-order-item-card">
                <div className="ongoing-order-item-card__row">
                  <div className="ongoing-order-item-card__info">
                    {order.userOrder.restroId && firstItem.menuId ? (
                      <MenuItemThumbnail
                        restroId={order.userOrder.restroId}
                        menuId={firstItem.menuId}
                        filename={order.menuCardList?.find((m) => m.id === firstItem.menuId)?.imageBig}
                        className="ongoing-order-item-card__thumb"
                      />
                    ) : (
                      <span className="ongoing-order-item-card__thumb" aria-hidden />
                    )}
                    <div>
                      <div className="ongoing-order-item-card__name">{firstItem.itemName}</div>
                      <OrderLineOptions optionsNote={firstItem.optionsNote} itemNote={firstItem.itemNote} />
                      <div className="ongoing-order-item-card__qty">
                        Qty: {firstItem.quantity} · {formatPrice(firstItem.totalPrice, order.userOrder.currencySymbol)}
                      </div>
                    </div>
                  </div>
                  <div className="ongoing-order-item-card__price">
                    {formatPrice(firstItem.totalPrice, order.userOrder.currencySymbol)}
                  </div>
                </div>
              </div>
              )}
              <p className="ongoing-order-hint">{statusHint}</p>
              {openPaymentId && (
                <button
                  type="button"
                  className="ongoing-order-cta ongoing-order-cta--ghost ongoing-order-cta--full"
                  onClick={handleCancelOpenPayment}
                  disabled={cancellingPayment}
                >
                  {cancellingPayment ? 'Cancelling…' : 'Cancel payment'}
                </button>
              )}
              <button type="button" className="ongoing-order-cta ongoing-order-cta--full" onClick={handleNavigate}>
                Go to Order Details →
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(barContent, getFixedChromePortalTarget());
};

export default OngoingOrderBar;
